// S-NRI-5 سطل الف — سطحِ «مسیرِ بازنشسته».
//
// تا پیش از این، بازنشستگی فقط «آرایشی» بود: نامِ مسیر از ناوبری/چیپ/⌘K فیلتر
// می‌شد ولی URL مستقیم (‎/admin/schools، ‎/tuition، ‎/apply …) همان صفحهٔ دانشگاهیِ
// مدرک‌ده را کامل رندر می‌کرد. این کامپوننت در AppShell (تنها گلوگاهِ رندر) جایِ
// آن صفحه می‌نشیند، پس بازنشستگی واقعی می‌شود بدون آنکه یک خط کد پاک شود.
//
// چرا «بازنشسته» نه «۴۰۳/۴۰۴»: مسئله دسترسیِ نقش نیست — این بخش در مدلِ آموزشِ
// ضمن خدمت اصلاً وجود ندارد. پس پیام باید همین را بگوید و کاربر را به جایگزینِ
// واقعی برساند، نه اینکه او را دنبالِ «مجوز گرفتن» بفرستد.
//
// برگشت‌پذیری: برداشتنِ نامِ مسیر از RETIRED_MODULES در config/adapt-flags.ts
// همین صفحه را بی‌کم‌وکاست برمی‌گرداند.
import React from "react";
import { Icon } from "../icons";
import { Button, EmptyState } from "../ui";
import { useRole } from "../role";
import { useGo } from "../router";
import { useAuth } from "../auth/AuthContext";

export interface RetiredRouteProps {
  /** شناسهٔ مسیرِ درخواست‌شده (برای زمینه). */
  routeId?: string;
}

/**
 * جایگزینِ معنادار برای خوشه‌های بازنشسته — تا کاربر بن‌بست نبیند.
 * کلیدها با پیشوندِ بلندتر اول می‌آیند تا «tuition/loans» پیش از «tuition» بگیرد.
 */
const REPLACEMENT: ReadonlyArray<{ prefix: string; note: string; to?: string; cta?: string }> = [
  {
    prefix: "admin/schools",
    note: "ساختارِ سازمانی سامانه، درختِ وزارت → هلدینگ → شرکت → واحد است، نه سلسله‌مراتبِ دانشکده‌ای.",
    to: "admin/organizations",
    cta: "ساختار سازمانی",
  },
  { prefix: "admin/faculties", note: "جایگزین: ساختارِ سازمانیِ وزارت نیرو.", to: "admin/organizations", cta: "ساختار سازمانی" },
  { prefix: "admin/departments", note: "جایگزین: ساختارِ سازمانیِ وزارت نیرو.", to: "admin/organizations", cta: "ساختار سازمانی" },
  { prefix: "admin/programs", note: "جایگزین: ساختارِ سازمانیِ وزارت نیرو.", to: "admin/organizations", cta: "ساختار سازمانی" },
  { prefix: "admin/specializations", note: "جایگزین: ساختارِ سازمانیِ وزارت نیرو.", to: "admin/organizations", cta: "ساختار سازمانی" },
  {
    prefix: "admin/degree-policies",
    note: "کارمندِ وزارت «مشروطی/اخراج/سنوات» ندارد؛ سنجهٔ او سقفِ ساعتِ آموزشِ سالانه و گواهیِ ساعت‌محور است.",
  },
  { prefix: "admin/regulation", note: "آیین‌نامهٔ مدرک‌ده جای خود را به «نظام آموزش کارمندان دولت» داده است." },
  { prefix: "admin/commission", note: "کمیسیونِ موارد خاصِ دانشگاهی در آموزش ضمن خدمت موضوعیت ندارد." },
  { prefix: "probation-review", note: "«مشروطی» در آموزش ضمن خدمت موضوعیت ندارد." },
  { prefix: "admin/stage-progress", note: "مسیرِ مرحله‌ایِ مدرک، جای خود را به مسیرِ شایستگی و رتبهٔ شغلی داده است." },
  {
    prefix: "admissions",
    note: "کارمندِ وزارت «پذیرش» نمی‌شود؛ شرکتِ متبوع او را برای دوره نامزد می‌کند.",
    to: "login",
    cta: "ورود به سامانه",
  },
  {
    prefix: "apply",
    note: "کارمندِ وزارت «درخواستِ پذیرش» نمی‌دهد؛ با حسابِ سازمانیِ خود وارد می‌شود.",
    to: "login",
    cta: "ورود به سامانه",
  },
  {
    prefix: "tuition",
    note: "در این سامانه هزینهٔ آموزش را شرکتِ متبوع می‌پردازد (کیف پول سازمانی)، نه فردِ فراگیر.",
  },
  { prefix: "financial-aid", note: "بورسیه/کمک‌هزینهٔ دانشگاهی جای خود را به بودجهٔ آموزشِ شرکت داده است." },
  {
    prefix: "admin/students",
    note: "این بخش در حال بازسازی با معنای «فهرست کارکنان/فراگیران» است.",
  },
  { prefix: "admin/instructors", note: "این بخش در حال بازسازی با معنای «فهرست مدرسان» است." },
  { prefix: "admin/terms", note: "این بخش در حال بازسازی با معنای «بازه‌های برنامهٔ آموزشیِ سالانه» است." },
  { prefix: "curriculum-builder", note: "این بخش در حال بازسازی با معنای «سرفصل‌سازِ دوره» است." },
  { prefix: "admin/student-overview", note: "پروندهٔ افراد از بخشِ «کاربران» در دسترس است.", to: "admin/users", cta: "کاربران" },
  { prefix: "admin/parent-links", note: "نقشِ «والد» در آموزشِ بزرگ‌سالانِ سازمانی موضوعیت ندارد." },
  { prefix: "admin/tuition-config", note: "پرداخت در این سامانه سازمانی است، نه شهریهٔ فردی." },
  { prefix: "admin/ministry-library", note: "مرجعِ بالادستِ این سامانه وزارت نیرو است." },
  { prefix: "admin/reference-data", note: "داده‌های مرجعِ آکادمیک جای خود را به کاتالوگِ شایستگی و رتبهٔ شغلی داده‌اند." },
  { prefix: "add-drop", note: "حذف و اضافهٔ ترمی جای خود را به نامزدسازی و تأییدِ مدیرِ آموزش داده است." },
  { prefix: "my-path", note: "جایگزین: «مسیر ارتقای شغلی من».", to: "career", cta: "مسیر ارتقای شغلی من" },
  { prefix: "my-chart", note: "جایگزین: «مسیر ارتقای شغلی من».", to: "career", cta: "مسیر ارتقای شغلی من" },
  { prefix: "network", note: "گفت‌وگوهای سامانه در «انجمن» متمرکز است.", to: "community", cta: "انجمن گفتگو" },
];

const matchReplacement = (routeId?: string): (typeof REPLACEMENT)[number] | undefined => {
  if (!routeId) return undefined;
  const seg = routeId.replace(/^\//, "");
  return REPLACEMENT.find((r) => seg === r.prefix || seg.startsWith(`${r.prefix}/`));
};

export const RetiredRoute = ({ routeId }: RetiredRouteProps): React.ReactElement => {
  const go = useGo();
  const auth = useAuth();
  const { role, activeRoleId } = useRole();
  const hit = matchReplacement(routeId);

  // مقصدِ بازگشت: جایگزینِ اختصاصی → خانهٔ نقش → صفحهٔ عمومی.
  const authed = auth.isAuthenticated && !!activeRoleId;
  const fallbackTo = authed ? role.homeRoute : "home";
  const fallbackCta = authed ? "بازگشت به خانهٔ من" : "بازگشت به صفحهٔ نخست";
  // مقصدِ جایگزین فقط وقتی پیشنهاد می‌شود که کاربر بتواند به آن برسد؛
  // مسیرهای کارتابلی برای کاربرِ مهمان بن‌بستِ تازه می‌سازند.
  const useHit = !!hit?.to && (authed || hit.to === "login");
  const to = useHit ? (hit as { to: string }).to : fallbackTo;
  const cta = useHit ? (hit?.cta ?? fallbackCta) : fallbackCta;

  return (
    <main
      id="retired-route"
      data-screen-label="بخشِ بازنشسته"
      className="page-shell"
      style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <EmptyState
        icon={<Icon name="info" size={26} />}
        title="این بخش در سامانهٔ آموزش ضمن خدمت وجود ندارد"
        body={
          <>
            این صفحه بخشی از مدلِ دانشگاهیِ مدرک‌ده بود و در سامانهٔ آموزش ضمن خدمتِ صنعت آب و برق
            کاربردی ندارد.
            {hit?.note ? ` ${hit.note}` : ""}
          </>
        }
        cta={
          <Button variant="primary" size="md" onClick={() => go(to)}>
            {cta}
          </Button>
        }
      />
    </main>
  );
};

export default RetiredRoute;
